import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.requires_2fa) {
            return { requires_2fa: true, challenge: data.challenge };
        }
        if (data.requires_2fa_setup) {
            return { requires_2fa_setup: true, setup_token: data.setup_token };
        }
        if (data.access_token) {
            localStorage.setItem("access_token", data.access_token);
        }
        setUser(data.user);
        return data.user;
    };

    const register = async (name, email, password, refCode = "", captchaToken = "") => {
    const { data } = await api.post(
        "/auth/register",
        { name, email, password, ref_code: refCode },
        captchaToken ? { headers: { "X-Turnstile-Token": captchaToken } } : undefined
    );
    if (data.requires_verification) {
        return { requires_verification: true, email: data.email };
    }
    if (data.access_token) {
        localStorage.setItem("access_token", data.access_token);
    }
    setUser(data.user);
    return data.user;
};

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_) {
            /* ignore */
        }
        localStorage.removeItem("access_token");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}